import React, { useMemo } from 'react';
import { StyleSheet, Text, View, type ViewStyle } from 'react-native';
import Ionicons from '@react-native-vector-icons/ionicons/static';
import { GTFonts, useTheme, withOpacity, type Theme } from '../../theme';
import { borderWidth, fs, iconSize, s, v } from '../../utils/libs';
import { useAppSelector } from '../../redux/helper';

type Props = {
  style?: ViewStyle;
  /**
   * `default` — full soft banner for forms.
   * `subtle` — single-line notice for checkout / payment success.
   */
  variant?: 'default' | 'subtle';
};

/**
 * Soft warning shown when the current location is the system-generated test
 * location. Does not block the form — practice adds remain allowed.
 */
const TestLocationBanner: React.FC<Props> = ({
  style,
  variant = 'default',
}) => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);
  const currentLocation = useAppSelector(state => state.location.currentLocation);

  if (currentLocation?.bp_location_is_system_generated !== true) {
    return null;
  }

  const locationName = String(currentLocation.bp_location_name || '').trim();

  if (variant === 'subtle') {
    return (
      <View
        style={[styles.subtle, style]}
        accessible
        accessibilityRole="text"
        accessibilityLabel="You are on a test location"
        testID="test-location-banner-subtle">
        <Ionicons
          name="flask-outline"
          size={iconSize(16)}
          color={theme.colors.warningDark}
        />
        <Text style={styles.subtleText}>
          This is a Test Location. Changes here do not affect your real business data
        </Text>
      </View>
    );
  }

  return (
    <View
      style={[styles.banner, style]}
      accessible
      accessibilityRole="text"
      accessibilityLabel="You are on a test location"
      testID="test-location-banner">
      <View style={styles.header}>
        <Ionicons
          name="flask-outline"
          size={iconSize(20)}
          color={theme.colors.warningDark}
        />
        <Text style={styles.title}>You are on a test location</Text>
      </View>
      <Text style={styles.body}>
        {locationName
          ? `${locationName} is a test location. Changes here do not affect your real business data.`
          : 'Changes here do not affect your real business data. Switch to a real location from the header when you are ready.'}
      </Text>
    </View>
  );
};

const createStyles = (theme: Theme) =>
  StyleSheet.create({
    banner: {
      backgroundColor: withOpacity(theme.colors.warning, 0.1),
      borderRadius: theme.borderRadius.md,
      borderWidth: borderWidth(1),
      borderColor: withOpacity(theme.colors.warning, 0.4),
      padding: s(14),
      gap: v(6),
      marginBottom: v(12),
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(8),
    },
    title: {
      flex: 1,
      fontFamily: GTFonts.semibold,
      fontSize: fs(15),
      lineHeight: fs(20),
      color: theme.colors.text,
    },
    body: {
      fontFamily: GTFonts.regular,
      fontSize: fs(13),
      lineHeight: fs(18),
      color: theme.colors.text,
    },
    subtle: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(8),
      paddingVertical: v(8),
      paddingHorizontal: s(12),
      borderRadius: theme.borderRadius.sm,
      backgroundColor: withOpacity(theme.colors.warning, 0.08),
      borderWidth: borderWidth(1),
      borderColor: withOpacity(theme.colors.warning, 0.28),
      marginBottom: v(12),
    },
    subtleText: {
      flex: 1,
      fontFamily: GTFonts.regular,
      fontSize: fs(12),
      lineHeight: fs(16),
      color: theme.colors.warningDark,
    },
  });

export default TestLocationBanner;
